import React, { useState } from 'react';
import SchemaForm, { FormField } from '../../components/Forms/SchemaForm';
import VerificationPageLayout from '../../components/Layout/VerificationPageLayout';
import VerificationResultDisplay from '../../components/Verification/VerificationResultDisplay';
import {
    cipcReportService,
    cipcSchema,
    CIPCReportPayload,
    CipcMatchItem,
    CIPCReportResponse,
} from '../../services/verificationSystemServices/cipcReportService';
import {
    Box,
    Alert,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
} from '@mui/material';
import ActionButton from '../../components/Button/ActionButton';

const CIPCReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Phase 1 — match results
    const [matches, setMatches] = useState<CipcMatchItem[]>([]);
    const [fetchingResultFor, setFetchingResultFor] = useState<string | null>(null); // enquiry_id being fetched

    // Phase 2 — final report
    const [report, setReport] = useState<CIPCReportResponse | null>(null);

    // Store search payload so we can re-use yourReference / voucherCode in Phase 2
    const [searchPayload, setSearchPayload] = useState<CIPCReportPayload | null>(null);

    const formSchema: FormField[] = [
        {
            name: 'businessName',
            label: 'Business Name',
            type: 'text',
            gridProps: { xs: 12, md: 12 },
            required: false,
        },
        { name: 'reg1', label: 'Reg1', type: 'text', gridProps: { xs: 12, md: 3 }, required: false },
        { name: 'reg2', label: 'Reg2', type: 'text', gridProps: { xs: 12, md: 3 }, required: false },
        { name: 'reg3', label: 'Reg3', type: 'text', gridProps: { xs: 12, md: 3 }, required: false },
        { name: 'vatNo', label: 'VAT Number', type: 'text', gridProps: { xs: 12, md: 6 }, required: false },
        { name: 'solePropIdNo', label: 'Sole Prop ID No', type: 'text', gridProps: { xs: 12, md: 6 }, required: false, maxLength: 13 },
        { name: 'yourReference', label: 'Your Reference', type: 'text', gridProps: { xs: 12, md: 6 }, required: false },
        { name: 'voucherCode', label: 'Voucher Code', type: 'text', gridProps: { xs: 12, md: 6 }, required: false },
    ];

    // ── Fetch the full report for a chosen candidate ──────────────────────────
    const fetchReport = async (match: CipcMatchItem) => {
        setFetchingResultFor(match.enquiry_result_id);
        setError(null);

        try {
            const response = await cipcReportService.getResult({
                enquiryId: parseInt(match.enquiry_id, 10),
                enquiryResultId: parseInt(match.enquiry_result_id, 10),
                yourReference: searchPayload?.yourReference || undefined,
                voucherCode: searchPayload?.voucherCode || undefined,
            });
            setReport(response);
            setMatches([]); // collapse the candidate table once we have a result
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.message || 'Failed to fetch CIPC report.');
        } finally {
            setFetchingResultFor(null);
        }
    };

    // ── Phase 1 submit ────────────────────────────────────────────────────────
    const handleSearchSubmit = async (data: any) => {
        setLoading(true);
        setError(null);
        setMatches([]);
        setReport(null);

        const payload = cipcReportService.createPayload(data as CIPCReportPayload);
        setSearchPayload(payload);

        try {
            const response = await cipcReportService.getMatch(payload);

            if (!response.success || response.matches.length === 0) {
                setError(response.message || 'No matching businesses found.');
                return;
            }

            if (response.matches.length === 1) {
                // Single match — skip the table, go straight to the report
                await fetchReport(response.matches[0]);
            } else {
                // Multiple matches — surface the selection table
                setMatches(response.matches);
            }
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <VerificationPageLayout title="CIPC Report">

            {/* ── Search Form ── */}
            <Box mb={4}>
                <SchemaForm
                    schema={formSchema}
                    onSubmit={handleSearchSubmit}
                    submitLabel={loading ? 'Searching…' : 'Search'}
                    validationSchema={cipcSchema}
                />
            </Box>

            {/* ── Loading spinner ── */}
            {(loading || fetchingResultFor !== null) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                    <CircularProgress sx={{ color: '#2D4677' }} />
                </Box>
            )}

            {/* ── Error banner ── */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* ── Candidate selection table (multiple matches) ── */}
            {matches.length > 1 && !report && (
                <Box mb={4}>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                        Select a business below to extract its full CIPC report.
                    </Typography>

                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #dce6f5', borderRadius: '12px', overflow: 'hidden' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#EFF3FB' }}>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Registration No
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Business Name
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Commercial ID
                                    </TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {matches.map((match, idx) => (
                                    <TableRow
                                        key={idx}
                                        hover
                                        sx={{ '&:last-child td': { borderBottom: 'none' } }}
                                    >
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>
                                            {match.registration_no || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1a1a2e' }}>
                                            {match.business_name || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>
                                            {match.commercial_id || '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <ActionButton
                                                size="small"
                                                disabled={fetchingResultFor !== null}
                                                onClick={() => fetchReport(match)}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    py: 0.75,
                                                    px: 2,
                                                }}
                                            >
                                                {fetchingResultFor === match.enquiry_result_id ? 'Fetching…' : 'Get Report'}
                                            </ActionButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* ── Report result ── */}
            {report && !loading && !fetchingResultFor && (
                <>
                    {matches.length === 0 && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => { setReport(null); setMatches([]); }}
                            sx={{
                                mb: 2,
                                borderColor: '#2D4677',
                                color: '#2D4677',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': { borderColor: '#1a365d', backgroundColor: 'rgba(45,70,119,0.04)' },
                            }}
                        >
                            ← New Search
                        </Button>
                    )}
                    <VerificationResultDisplay
                        title="CIPC Report"
                        requestId={report.log_id}
                        success={report.success}
                        message={report.message}
                        data={report.report_data ?? null}
                    />
                </>
            )}

        </VerificationPageLayout>
    );
};

export default CIPCReport;
